import PropTypes from 'prop-types';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { DISCORD_PLAYBACK_DISABLED_TEXT } from 'src/common/components/player/actionDrawer/DiscordControls/constants';
import withSyncedPlayerBehavior from 'src/common/decorators/withSyncedPlayerBehavior';
import { useStores } from 'src/common/providers';
import { selectDiscordState, selectIsDiscord, selectIsMobile } from 'src/common/selectors/app';
import cssVariables from 'src/common/styles/variables';
import { playerStatuses } from '../../constants/playerStatuses';
import useMobilePopout from '../../hooks/useMobilePopout';
import { isMedium } from '../../utils/breakpoints';
import { getCssStyle } from '../../utils/getCssStyle';
import gtagTrack from '../../utils/gtagTrack';
import { playerStatusToSvgIconMap } from '../../utils/playerStatusToIconMap';
import GuideItemLink from '../containerItems/shared/GuideItemLink';
import MessagePopoverWithTail from '../messagePopoverWithTail/MessagePopoverWithTail';
import PopOutButton from '../shared/button/PopOutButton';
import CircularProgress from '../shared/CircularProgress';
import css from './playButton.module.scss';

const LARGE_BUTTON = 72;
const MEDIUM_BUTTON = 56;
const SMALL_BUTTON = 48;

export const PlayButton = ({
  canScrub,
  bannerImage,
  syncedPlayerStatus,
  handlePlayButtonClick,
  guideItem = {},
  breakpoint,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const isDiscord = useSelector(selectIsDiscord);
  const isMobile = useSelector(selectIsMobile);
  const discordState = useSelector(selectDiscordState);
  const { playerStore } = useStores();
  const tunedGuideId = playerStore.currentGuideItem()?.playableGuideId;
  const { canShowPopover, handleTargetClick } = useMobilePopout(isMobile, 3000);
  const nowPlayingRejectReasonKey = playerStore.metadata()?.rejectReason;
  const isPopoverOpen = Boolean(
    isDiscord &&
      (!discordState?.canControlPlayback ||
        (nowPlayingRejectReasonKey && guideItem.guideId === tunedGuideId)) &&
      ((isMobile && canShowPopover) || (!isMobile && isHovering)),
  );
  const popout = useStores().playerStore.popout();

  const getButtonSize = () => {
    if (isDiscord && !!bannerImage) {
      return MEDIUM_BUTTON;
    }

    if (!isMedium(breakpoint)) {
      return LARGE_BUTTON;
    }

    return SMALL_BUTTON;
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const buttonSize = getButtonSize();

  if (
    syncedPlayerStatus === playerStatuses.connecting ||
    syncedPlayerStatus === playerStatuses.waiting
  ) {
    return (
      <div className={css.playButton}>
        <CircularProgress
          data-testid="profilePlayerLoadingSpinner"
          size={buttonSize}
          color={getCssStyle('--text-color')}
          thickness={2.5}
        />
      </div>
    );
  }

  if ((syncedPlayerStatus && syncedPlayerStatus === playerStatuses.popout) || popout.showButton) {
    return (
      <PopOutButton
        width={`${buttonSize}px`}
        height={`${buttonSize}px`}
        dataTestId="profilePopOutButton"
      />
    );
  }

  const PlayButtonStateRepresentation = playerStatusToSvgIconMap[syncedPlayerStatus]?.(canScrub);

  return PlayButtonStateRepresentation ? (
    <GuideItemLink
      guideItem={guideItem}
      onClick={(...args) => {
        gtagTrack.profilePlayButtonClick();
        handlePlayButtonClick(...args);
      }}
    >
      <div
        className={css.playButtonWrapper}
        onClick={handleTargetClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <MessagePopoverWithTail
          message={
            guideItem.guideId === tunedGuideId
              ? nowPlayingRejectReasonKey || DISCORD_PLAYBACK_DISABLED_TEXT
              : DISCORD_PLAYBACK_DISABLED_TEXT
          }
          isOpen={isPopoverOpen}
        />
        <PlayButtonStateRepresentation
          id="profilePlayerActionButton"
          dataTestId="profilePlayerActionButton"
          width={`${buttonSize}px`}
          height={`${buttonSize}px`}
          fillButton={isDiscord ? cssVariables['--secondary-color-5'] : ''}
          fillButtonBackground={isDiscord ? cssVariables['--white'] : ''}
        />
      </div>
    </GuideItemLink>
  ) : null;
};

PlayButton.propTypes = {
  syncedPlayerStatus: PropTypes.string.isRequired,
  handlePlayButtonClick: PropTypes.func.isRequired,
  bannerImage: PropTypes.string,
  guideItem: PropTypes.object.isRequired,
  breakpoint: PropTypes.number.isRequired,
  canScrub: PropTypes.bool,
};

export default withSyncedPlayerBehavior(PlayButton);
